// @flow
import update from 'immutability-helper';
import { ofType, StateObservable } from 'redux-observable';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { getFeaturedProductsApi } from './api';
import { debounceTime } from 'rxjs/operators';
import Paginator from '../../common/Paginator';
import { encodeProduct } from './utils/encode';

import type { HTTPError } from '../../error';
import type { RootState } from '../index';
import type { Category } from '../../entities';

export type Product = {
  type: string;
  id: string;
  name: string;
  provider: string;
  price: string;
  url: string;
  image: string;
  freeShipping: boolean;
  taxFree: boolean;
  weight: string;
  categoryId: string;
  category?: Category;
  amount: number;
  detail?: string;
  encoded?: string;
  originalPackaging: boolean;
  volumeTier?: {
    code: string;
    description: string;
    image: string;
    type: string;
  };
  onlineStore?: {
    id: string;
    logo: string;
    name: string;
    slug: string;
    url: string;
  };
  originType?: string;
};

export type ProductsFilter = {
  categories?: string[];
  minPrice?: number;
  maxPrice?: number;
  stores?: string[];
  freeShipping?: boolean;
  freeTax?: boolean;
};

type FeaturedProductsAction = {
  +type: string;
  +products?: Product[];
  +filters?: ProductsFilter;
  +error?: HTTPError;
  +resetPagination?: boolean;
};

export type FeaturedProductsState = {
  isFetchingProducts?: boolean;
  fetchProductsError?: HTTPError;
  products?: Product[];
  page?: number;
  pageCanAsk?: boolean;
};

const defaultDebounceTime = 800;

// Actions
const FEATURED_PRODUCT_FETCH_REQUEST = 'FEATURED_PRODUCT_FETCH_REQUEST';
const FEATURED_PRODUCT_FETCH_SUCCESS = 'FEATURED_PRODUCT_FETCH_SUCCESS';
const FEATURED_PRODUCT_FETCH_ERROR = 'FEATURED_PRODUCT_FETCH_ERROR';
const FEATURED_PRODUCT_RESET = 'FEATURED_PRODUCT_RESET';

export function fetchFeaturedProducts(filters: ProductsFilter, resetPagination?: boolean): FeaturedProductsAction {
  return {
    type: FEATURED_PRODUCT_FETCH_REQUEST,
    filters,
    resetPagination,
  }
}

export function fetchFeaturedProductsSuccess(products: Product[]): FeaturedProductsAction {
  return {
    type: FEATURED_PRODUCT_FETCH_SUCCESS,
    products,
  }
}

export function fetchFeaturedProductsError(error: HTTPError): FeaturedProductsAction {
  return {
    type: FEATURED_PRODUCT_FETCH_ERROR,
    error,
  }
}

export function resetState(): FeaturedProductsAction {
  return {
    type: FEATURED_PRODUCT_RESET,
  }
}

export const actions = {
  FEATURED_PRODUCT_FETCH_REQUEST,
  FEATURED_PRODUCT_FETCH_SUCCESS,
  FEATURED_PRODUCT_FETCH_ERROR,
  FEATURED_PRODUCT_RESET,
  fetchFeaturedProducts,
  fetchFeaturedProductsSuccess,
  fetchFeaturedProductsError,
  resetState,
};

export const initialState: FeaturedProductsState = {};

const paginator = new Paginator(['featuredProducts']);

export default function reducer(state: FeaturedProductsState = initialState,
                                action: FeaturedProductsAction): FeaturedProductsState {
  switch (action.type) {
    case FEATURED_PRODUCT_FETCH_REQUEST:
      if (action.resetPagination) {
        paginator.reset('featuredProducts');
        return update(state, {
          isFetchingProducts: { $set: true },
          products: { $set: paginator.getItems('featuredProducts') },
          page: { $set: paginator.getPage('featuredProducts') },
          pageCanAsk: { $set: paginator.getCanAsk('featuredProducts') },
        });
      }
      return {
        ...state,
        isFetchingProducts: true,
      };

    case FEATURED_PRODUCT_FETCH_SUCCESS:
      paginator.increment('featuredProducts', action.products, state.products);

      return update(state, {
        products: { $set: paginator.getItems('featuredProducts') },
        page: { $set: paginator.getPage('featuredProducts') },
        pageCanAsk: { $set: paginator.getCanAsk('featuredProducts') },
        $unset: ['isFetchingProducts'],
      });
    case FEATURED_PRODUCT_FETCH_ERROR:
      return update(state, {
        fetchStoresError: { $set: action.error },
        $unset: ['isFetchingProducts'],
      });
    case FEATURED_PRODUCT_RESET:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

export function fetchFeaturedProductsEpic(action$: Observable<FeaturedProductsAction>,
                                          state$: StateObservable<RootState>): Observable<FeaturedProductsAction> {
  return action$.pipe(
    ofType(FEATURED_PRODUCT_FETCH_REQUEST),
    debounceTime(defaultDebounceTime),
    mergeMap(({ filters }) => {
      return getFeaturedProductsApi({
        ...filters,
        page: state$.value.featuredProducts.page || paginator.getPage('featuredProducts'),
        limit: paginator.getLimit(),
      }).pipe(
        takeUntil(action$.pipe(ofType(FEATURED_PRODUCT_RESET))),
        takeUntil(action$.pipe(ofType(FEATURED_PRODUCT_FETCH_REQUEST))),
        map((response: Product[]) => {
          const newProducts = response.map((p: Product, index: number) => ({
            ...p,
            encoded: encodeProduct(p),
          }));
          return fetchFeaturedProductsSuccess(newProducts)
        }),
        catchError((e: HTTPError) => of(fetchFeaturedProductsError(e))))
    }));
}



// WEBPACK FOOTER //
// ./src/modules/featured-products/duck.js